"use client";
import { Button } from "@/components/ui/Button";
import ViewportHeightWrapper from "@/components/ViewportHeightWrapper";
import {
  CABIN_CLASSES,
  PASSENGER_TYPES,
  defaultCabinClass,
  defaultPassengerCounts,
} from "@/constants/passengers";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useKeyPressed } from "@/hooks/useKeyPressed";
import { useLockBodyScroll } from "@/hooks/useLockBodyScroll";
import { useOnClickOutside } from "@/hooks/useOnClickOutside";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { z } from "zod";

const FlightPassengersAndCabinClass = ({
  id,
  label,
  value,
  onSelect,
  placeholder,
  isMultiTrip,
  setErrors,
  viewportHeightWrapperActive = false,
  scrollToForm,
  usePortalForDropdown = false,
  passengersDisabled = false,
}) => {
  const isMobile = useIsMobile();
  const t = useTranslationDetector();

  // İlk render'da dışarıdan gelen value ile initialize et
  const [passengerCounts, setPassengerCounts] = useState(
    value?.passengers || defaultPassengerCounts,
  );
  const [cabinClass, setCabinClass] = useState(
    value?.cabinClass || defaultCabinClass,
  );

  const [isOpen, setIsOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [portalDropdownPosition, setPortalDropdownPosition] = useState({
    top: 0,
    left: 0,
  });

  // Portal ve animasyon state'leri
  const [shouldRenderPortal, setShouldRenderPortal] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldRenderWebDropdown, setShouldRenderWebDropdown] = useState(false);
  const [isWebDropdownAnimating, setIsWebDropdownAnimating] = useState(false);

  const escapePressed = useKeyPressed("Escape");
  const containerRef = useRef(null);
  const webDropdownPortalRef = useRef(null);

  // İlk render'ı takip et
  const isFirstRender = useRef(true);

  // Callback'leri ref'e kaydet
  const onSelectRef = useRef(onSelect);
  const setErrorsRef = useRef(setErrors);
  const tRef = useRef(t);

  useEffect(() => {
    onSelectRef.current = onSelect;
  }, [onSelect]);

  useEffect(() => {
    setErrorsRef.current = setErrors;
  }, [setErrors]);

  useEffect(() => {
    tRef.current = t;
  }, [t]);

  const handleClickOutside = useCallback(() => {
    if (!isMobile && isOpen) {
      setIsOpen(false);
    }
  }, [isMobile, isOpen]);

  useEffect(() => {
    if (escapePressed && isOpen) {
      setIsOpen(false);
    }
  }, [escapePressed, isOpen]);

  useOnClickOutside(
    usePortalForDropdown ? [containerRef, webDropdownPortalRef] : containerRef,
    handleClickOutside,
  );
  useLockBodyScroll(shouldRenderPortal && isMobile);

  // Portal dropdown pozisyonu - fixed viewport koordinatları
  const updatePortalPosition = useCallback(() => {
    if (usePortalForDropdown && containerRef.current && isOpen && !isMobile) {
      const rect = containerRef.current.getBoundingClientRect();
      const dropdownWidth = 318;
      const margin = 8;
      let left = rect.left + rect.width / 2 - dropdownWidth / 2;
      const top = rect.bottom + margin;
      left = Math.max(
        16,
        Math.min(left, window.innerWidth - dropdownWidth - 16),
      );
      setPortalDropdownPosition({ top, left });
    }
  }, [usePortalForDropdown, isOpen, isMobile]);

  useEffect(() => {
    if (
      usePortalForDropdown &&
      !isMobile &&
      shouldRenderWebDropdown &&
      containerRef.current
    ) {
      updatePortalPosition();
      window.addEventListener("scroll", updatePortalPosition, true);
      window.addEventListener("resize", updatePortalPosition);
      return () => {
        window.removeEventListener("scroll", updatePortalPosition, true);
        window.removeEventListener("resize", updatePortalPosition);
      };
    }
  }, [
    usePortalForDropdown,
    isMobile,
    shouldRenderWebDropdown,
    updatePortalPosition,
  ]);

  // Portal ve animasyon kontrolü
  useEffect(() => {
    if (isOpen && isMobile) {
      setShouldRenderPortal(true);
      const timer = setTimeout(() => {
        setIsAnimating(true);
      }, 10);
      return () => clearTimeout(timer);
    } else if (!isOpen && isMobile) {
      setIsAnimating(false);
      const timer = setTimeout(() => {
        setShouldRenderPortal(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen, isMobile]);

  // Web dropdown animasyon kontrolü
  useEffect(() => {
    if (isOpen && !isMobile) {
      setShouldRenderWebDropdown(true);
      const timer = requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsWebDropdownAnimating(true);
        });
      });
      return () => cancelAnimationFrame(timer);
    } else if (!isOpen && !isMobile) {
      setIsWebDropdownAnimating(false);
      const timer = setTimeout(() => {
        setShouldRenderWebDropdown(false);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen, isMobile]);

  // Sadece ilk render'da value'yu al, sonraki değişikliklerde alma
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      if (value) {
        setPassengerCounts(value.passengers || defaultPassengerCounts);
        setCabinClass(value.cabinClass || defaultCabinClass);
      }
    }
  }, []);

  // Validate and auto-apply changes when passengers or cabin class change
  useEffect(() => {
    // İlk render'da validasyon yapma
    if (isFirstRender.current) {
      return;
    }

    // Validation schema - t fonksiyonunu ref'ten al
    const passengerCountsSchema = z
      .object({
        [PASSENGER_TYPES.ADULT]: z.number().min(0).max(7),
        [PASSENGER_TYPES.STUDENT]: z.number().min(0).max(7),
        [PASSENGER_TYPES.CHILD]: z.number().min(0).max(7),
        [PASSENGER_TYPES.INFANT]: z.number().min(0).max(7),
        // [PASSENGER_TYPES.ELDERLY]: z.number().min(0).max(7),
      })
      .refine(
        (data) => {
          const totalNonInfant = data.adult + data.student + data.child;
          return totalNonInfant <= 7;
        },
        {
          message: tRef.current("common.maxSevenPassengersExcludingInfants"),
          path: ["general"],
        },
      )
      .refine(
        (data) => {
          const responsibleAdults = data.adult + data.student;
          return responsibleAdults >= 1;
        },
        {
          message: tRef.current("common.atLeastOneAdultRequired"),
          path: ["adult"],
        },
      )
      .refine(
        (data) => {
          if (data.infant > 0) {
            const responsibleAdults = data.adult;
            return responsibleAdults >= 1;
          }
          return true;
        },
        {
          message: tRef.current("common.infantsMustTravelWithAdult"),
          path: ["infant"],
        },
      )
      .refine(
        (data) => {
          if (data.child > 0) {
            const responsibleAdults = data.adult + data.student;
            return responsibleAdults >= 1;
          }
          return true;
        },
        {
          message: tRef.current("common.childrenMustTravelWithAdult"),
          path: ["child"],
        },
      )
      .refine(
        (data) => {
          const responsibleForInfants = data.adult;
          return data.infant <= responsibleForInfants;
        },
        {
          message: tRef.current("common.oneInfantPerAdult"),
          path: ["infant"],
        },
      );

    const passengerSelectionSchema = z.object({
      passengers: passengerCountsSchema,
      cabinClass: z.enum([CABIN_CLASSES.ECONOMY, CABIN_CLASSES.BUSINESS]),
    });

    const result = passengerSelectionSchema.safeParse({
      passengers: passengerCounts,
      cabinClass: cabinClass,
    });

    if (!result.success) {
      const errors = {};
      result.error.errors.forEach((error) => {
        errors[error.path[error.path.length - 1]] = error.message;
      });
      setValidationErrors(errors);
      setErrorsRef.current?.(errors);
    } else {
      setValidationErrors({});
      setErrorsRef.current?.({});

      // Validasyon geçerliyse otomatik olarak uygula
      onSelectRef.current?.({
        passengers: passengerCounts,
        cabinClass: cabinClass,
      });
    }
  }, [passengerCounts, cabinClass]); // t'yi dependency'den çıkardık

  const handlePassengerCountChange = useCallback((type, delta) => {
    setPassengerCounts((prev) => {
      const newCount = Math.max(0, Math.min(7, prev[type] + delta));
      return {
        ...prev,
        [type]: newCount,
      };
    });
  }, []);

  const handleCabinClassChange = useCallback((newClass) => {
    setCabinClass(newClass);
  }, []);

  const handleApply = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handlePortalClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleClick = useCallback(() => {
    if (passengersDisabled) return;
    if (!isOpen && scrollToForm) {
      if (!isMobile) {
        const inputRect = containerRef.current.getBoundingClientRect();
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop;
        const targetPosition = inputRect.top + scrollTop - 200;

        window.scrollTo({
          top: targetPosition,
          behavior: "smooth",
        });
      }
    }
    setIsOpen(!isOpen);
  }, [isOpen, isMobile, passengersDisabled]);

  // Calculate total passengers and display text
  const totalPassengers = useMemo(() => {
    return Object.values(passengerCounts).reduce(
      (sum, count) => sum + count,
      0,
    );
  }, [passengerCounts]);

  const displayValue = useMemo(() => {
    const cabinText =
      cabinClass === CABIN_CLASSES.BUSINESS
        ? t("common.business")
        : t("common.economy");
    return `${totalPassengers} ${t("common.passenger")}, ${cabinText}`;
  }, [totalPassengers, cabinClass, t]);

  // CSS class'ları memoize et
  const containerClasses = useMemo(
    () =>
      clsx(
        "border-primary-weak md:shadow-medium h-[68px] border-b bg-white duration-300 ease-in-out md:h-[66px] md:border-0",
        passengersDisabled
          ? "pointer-events-none cursor-not-allowed opacity-70"
          : "cursor-pointer",
        isOpen && "md:ring-primary md:ring-2",
        !isMultiTrip ? "md:rounded-lg" : "rounded-lg",
      ),
    [isOpen, isMultiTrip, passengersDisabled],
  );

  const labelClasses = useMemo(
    () =>
      clsx(
        "text-foreground-muted pointer-events-none absolute left-[18px] transition-all duration-300 ease-in-out md:left-[21px]",
        isOpen || displayValue
          ? "top-[14.5px] text-xs"
          : "top-1/2 -translate-y-1/2 text-base",
      ),
    [isOpen, displayValue],
  );

  const buttonClasses = useMemo(
    () =>
      clsx(
        "h-full w-full truncate rounded-lg border-0 bg-transparent px-[18px] text-left font-semibold transition-all duration-300 ease-in-out focus:ring-0 focus:outline-none md:px-5",
        !passengersDisabled && "cursor-pointer",
        isOpen || displayValue
          ? "pt-[26px] pb-[12px] md:pt-[31.5px] md:pb-[13.5px]"
          : "py-[12px] md:py-[22.5px]",
      ),
    [isOpen, displayValue, passengersDisabled],
  );

  const overlayClasses = useMemo(() => {
    return clsx(
      "fixed inset-0 z-50 bg-black/50",
      "transition-opacity duration-300",
      isAnimating ? "opacity-100" : "opacity-0",
    );
  }, [isAnimating]);

  const portalContainerClasses = useMemo(() => {
    return clsx(
      "fixed right-0 bottom-0 left-0 z-50 flex flex-col rounded-t-2xl bg-white",
      "transform transition-all duration-300",
      isAnimating ? "translate-y-0 ease-out" : "translate-y-full ease-in",
    );
  }, [isAnimating]);

  const webDropdownClasses = useMemo(() => {
    const baseClasses = clsx(
      "passenger-selector shadow-xxl z-50 w-[318px] rounded-lg bg-white p-0",
      "origin-top transform transition-all duration-200 ease-out",
      isWebDropdownAnimating
        ? "translate-y-0 scale-100 opacity-100"
        : "-translate-y-2 scale-95 opacity-0",
    );
    if (usePortalForDropdown) {
      return clsx(baseClasses, "fixed");
    }
    return clsx(
      baseClasses,
      "absolute top-full left-1/2 mt-2 -translate-x-1/2",
    );
  }, [isWebDropdownAnimating, usePortalForDropdown]);

  const isValid = Object.keys(validationErrors).length === 0;

  return (
    <>
      <div
        className={clsx(
          "flight-passengers-and-cabin-class-container relative",
          isMultiTrip
            ? "min-w-full md:min-w-[180px]"
            : "mt-[3px] min-w-[180px] md:mt-0",
        )}
        ref={containerRef}
      >
        <div className={containerClasses} onClick={handleClick}>
          {label && (
            <label htmlFor={id} className={labelClasses}>
              {label}
            </label>
          )}
          <button
            type="button"
            id={id}
            className={buttonClasses}
            aria-disabled={passengersDisabled}
          >
            {displayValue || placeholder}
          </button>
        </div>
        {/* Web dropdown - inline (parent overflow'a tabi) */}
        {!isMobile && shouldRenderWebDropdown && !usePortalForDropdown && (
          <div className={webDropdownClasses}>
            <ViewportHeightWrapper
              enabled={viewportHeightWrapperActive}
              defaultMaxHeight={470}
              bottomPadding={16}
            >
              <PassengerSelector
                passengerCounts={passengerCounts}
                cabinClass={cabinClass}
                onPassengerCountChange={handlePassengerCountChange}
                onCabinClassChange={handleCabinClassChange}
                onApply={handleApply}
                validationErrors={validationErrors}
                isValid={isValid}
              />
            </ViewportHeightWrapper>
          </div>
        )}
      </div>

      {/* Web dropdown portal - parent overflow'tan bağımsız */}
      {usePortalForDropdown &&
        !isMobile &&
        shouldRenderWebDropdown &&
        createPortal(
          <div
            ref={webDropdownPortalRef}
            className={webDropdownClasses}
            style={{
              top: portalDropdownPosition.top,
              left: portalDropdownPosition.left,
            }}
          >
            <ViewportHeightWrapper
              enabled={viewportHeightWrapperActive}
              defaultMaxHeight={470}
              bottomPadding={16}
            >
              <PassengerSelector
                passengerCounts={passengerCounts}
                cabinClass={cabinClass}
                onPassengerCountChange={handlePassengerCountChange}
                onCabinClassChange={handleCabinClassChange}
                onApply={handleApply}
                validationErrors={validationErrors}
                isValid={isValid}
              />
            </ViewportHeightWrapper>
          </div>,
          document.body,
        )}

      {/* Mobile portal - overlay ile slide up modal */}
      {shouldRenderPortal &&
        isMobile &&
        createPortal(
          <>
            {/* Overlay */}
            <div className={overlayClasses} onClick={handlePortalClose} />

            {/* Modal Content */}
            <div className={portalContainerClasses}>
              {/* Header */}
              <div className="flex shrink-0 items-center justify-between px-4 py-5">
                <span className="w-full text-center text-lg font-semibold">
                  {label}
                </span>
                <button onClick={handlePortalClose} className="self-end">
                  <FontAwesomeIcon
                    icon={byPrefixAndName.far["xmark"]}
                    className="text-2xl"
                  />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto">
                <PassengerSelector
                  passengerCounts={passengerCounts}
                  cabinClass={cabinClass}
                  onPassengerCountChange={handlePassengerCountChange}
                  onCabinClassChange={handleCabinClassChange}
                  onApply={handleApply}
                  validationErrors={validationErrors}
                  isValid={isValid}
                />
              </div>
            </div>
          </>,
          document.body,
        )}
    </>
  );
};

// Passenger selector component for the modal content
const PassengerSelector = ({
  passengerCounts,
  cabinClass,
  onPassengerCountChange,
  onCabinClassChange,
  onApply,
  validationErrors,
  isValid,
}) => {
  const t = useTranslationDetector();

  const passengerTypes = [
    {
      type: PASSENGER_TYPES.ADULT,
      label: t("common.passengerTypes0"),
      description: "  ",
    },
    {
      type: PASSENGER_TYPES.STUDENT,
      label: t("common.passengerTypes3"),
      description: t("common.passengerDescriptions3"),
    },
    {
      type: PASSENGER_TYPES.CHILD,
      label: t("common.passengerTypes1"),
      description: t("common.passengerDescriptions1"),
    },
    {
      type: PASSENGER_TYPES.INFANT,
      label: t("common.passengerTypes2"),
      description: t("common.passengerDescriptions2"),
    },
  ];

  return (
    <div className="flex flex-col">
      {/* Validation errors */}
      {Object.keys(validationErrors).length > 0 && (
        <div className="p-3">
          <div className="border-danger-subtle bg-danger-weaker flex items-start justify-start gap-2 rounded-lg border p-4">
            <FontAwesomeIcon
              icon={byPrefixAndName.far["circle-exclamation"]}
              className="text-danger mt-[5px]"
            />
            <div>
              {Object.values(validationErrors).map((error, index) => (
                <div key={index}>{error}</div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Passenger counters */}
      <div>
        {passengerTypes.map(({ type, label, description }) => (
          <div
            key={type}
            className="border-primary-weak flex min-h-[77px] items-center justify-between border-b p-4"
          >
            <div className="flex flex-col">
              <span className="flex-1 font-medium">{label}</span>
              <span className="text-foreground-muted text-sm">
                {description}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => onPassengerCountChange(type, -1)}
                disabled={passengerCounts[type] === 0}
                className="bg-primary disabled:text-foreground-muted disabled:bg-background-500 relative flex h-7 w-7 cursor-pointer items-center justify-center rounded-full text-white transition-colors disabled:cursor-not-allowed"
              >
                <FontAwesomeIcon
                  icon={byPrefixAndName.fak["minus"]}
                  className="text-[20px]"
                />
              </button>
              <span className="w-5 text-center font-medium">
                {passengerCounts[type]}
              </span>
              <button
                type="button"
                onClick={() => onPassengerCountChange(type, 1)}
                disabled={passengerCounts[type] === 7}
                className="bg-primary disabled:text-foreground-muted disabled:bg-background-500 flex h-7 w-7 cursor-pointer items-center justify-center rounded-full text-white transition-colors disabled:cursor-not-allowed"
              >
                <FontAwesomeIcon
                  icon={byPrefixAndName.fak["plus"]}
                  className="text-[20px]"
                />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Cabin class selection */}
      <div className="border-primary-weak border-b p-4">
        <div className="flex gap-2">
          <button
            onClick={() => onCabinClassChange(CABIN_CLASSES.ECONOMY)}
            className={clsx(
              "flex-1 cursor-pointer rounded-lg border px-4 py-3 text-center text-sm transition-colors",
              cabinClass === CABIN_CLASSES.ECONOMY
                ? "border-primary bg-primary-weaker text-primary"
                : "border-primary-weak hover:border-primary hover:bg-primary-weak",
            )}
          >
            {t("common.economy")}
          </button>
          <button
            onClick={() => onCabinClassChange(CABIN_CLASSES.BUSINESS)}
            className={clsx(
              "flex-1 cursor-pointer rounded-lg border px-4 py-3 text-center text-sm transition-colors",
              cabinClass === CABIN_CLASSES.BUSINESS
                ? "border-primary bg-primary-weaker text-primary"
                : "border-primary-weak hover:border-primary hover:bg-primary-weak",
            )}
          >
            {t("common.business")}
          </button>
        </div>
      </div>

      {/* Okay button - sadece modalı kapatıyor */}
      <div className="p-4">
        <Button onClick={onApply} size="lg" className="w-full">
          {t("common.okay")}
        </Button>
      </div>
    </div>
  );
};

FlightPassengersAndCabinClass.displayName = "FlightPassengersAndCabinClass";

export default FlightPassengersAndCabinClass;
